const RETRYABLE_GRAPH_STATUSES = new Set([404, 405, 502, 503, 504]);

function normalizeRoute(path) {
  const value = typeof path === 'string' ? path.trim() : '';
  if (!value) throw new Error('A graph endpoint path is required.');
  if (/^https?:\/\//i.test(value)) {
    try {
      return new URL(value).pathname || '/query';
    } catch (error) {
      throw new Error('The graph endpoint URL is invalid.');
    }
  }
  return value.startsWith('/') ? value : `/${value}`;
}

function browserOrigin() {
  if (typeof window === 'undefined' || !window.location || !window.location.origin) return '';
  return window.location.origin;
}

function configuredInferenceBase() {
  if (typeof window === 'undefined') return '';
  const configured = window.fixtures && window.fixtures.inf_server_url;
  return typeof configured === 'string' ? configured.trim().replace(/\/$/, '') : '';
}

function isMixedContentTarget(candidate) {
  if (typeof window === 'undefined' || !window.location) return false;
  try {
    const target = new URL(candidate, browserOrigin() || undefined);
    return window.location.protocol === 'https:' && target.protocol === 'http:';
  } catch (error) {
    return true;
  }
}

export function graphEndpointCandidates(path) {
  const route = normalizeRoute(path);
  const candidates = [route];
  const configuredBase = configuredInferenceBase();
  if (configuredBase) candidates.push(`${configuredBase}${route}`);

  const seen = new Set();
  return candidates.filter(candidate => {
    if (isMixedContentTarget(candidate)) return false;
    let identity = candidate;
    try {
      identity = new URL(candidate, browserOrigin() || 'http://localhost').href;
    } catch (error) {
      return false;
    }
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
}

/**
 * Fetch graph routes from the page origin first. Both web applications mount
 * /query and /edit, so this avoids CORS, mixed-content, and machine-local URLs.
 * The configured inference host remains a fallback for split deployments.
 */
export async function fetchGraphEndpoint(path, options) {
  const candidates = graphEndpointCandidates(path);
  const failures = [];

  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const hasFallback = index + 1 < candidates.length;
    try {
      const response = await fetch(candidate, options);
      if (hasFallback && RETRYABLE_GRAPH_STATUSES.has(Number(response.status))) {
        failures.push(`${candidate} returned ${response.status}`);
        continue;
      }
      return response;
    } catch (error) {
      failures.push(`${candidate}: ${error && error.message ? error.message : 'network error'}`);
      if (!hasFallback) break;
    }
  }

  const error = new Error(
    'The chart service could not be reached. Check your connection and retry; your query is still available.'
  );
  error.graphRequestFailures = failures;
  throw error;
}

